/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {InputProps as InputPropsType} from '@material-ui/core/Input';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import Autocomplete, {AutocompleteInputChangeReason, AutocompleteProps} from '@material-ui/lab/Autocomplete';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import isObject from 'lodash.isobject';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalSearchMapping} from '@ion/core/src/services/GlobalSearch';
import {AssetType, GenericObject} from '@ion/core/src/types/entities';
import {SearchType} from '@ion/core/src/types/services';
import JobSearchEngineTooltip from '@ion/features/src/search/JobSearchEngineTooltip';
import {TopNavSearchIcon} from '../../icons/TopNavSearchIcon';
import Render from '../../render/Render';
import {dlsAlt, dlsColor, dlsZIndex} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import TextField from '../textfield/TextField';
import AutocompleteSearchChip from './AutocompleteSearchChip';
import AutocompleteSearchResult from './AutocompleteSearchResult';

export interface Option {
  item?: any;
  name?: string;
  text?: string;
  type?: SearchType;
  timestamp?: number;
  details?: any;
  hasList?: boolean;
  isAsset?: boolean;
  list?: string;
  isSciDevice?: boolean;
  isFcDevice?: boolean;
  displayName?: string;
};

export interface SearchValue {
  event: ChangeEvent<{}>,
  value: string,
  reason: AutocompleteInputChangeReason
}

type AutocompleteSearchType = {
  options: Option[];
  searchText?: string;
  isJobSearch?: boolean;
  chipsEnabled?: boolean;
  chipFieldLabels?: GenericObject;
  filterChips?: {field: string, keyword: string, disableDelete?: boolean}[];
  onDelete?: (fields: {field: string, keyword: string, disableDelete?: boolean}) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onResultSelect?: (
    event: ChangeEvent<{}>,
    value: unknown | unknown[],
    reason: string
  ) => void;
  onChange?: (
    event: ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  fullWidthOnOpen?: boolean;
  width?: string | number;
  portalClassOverride?: string;
  disablePopperPortal?: boolean;
  loading?: boolean;
  loadingText?: string;
  noResultsText?: string;
  recentSearches: Option[];
  recentSearchesTitle?: string;
  shortPlaceholder?: boolean;
  overrideTitleStyles?: boolean;
  inputLength?: number;
  isCtcAdminGlobalSearch?: boolean;
  isGlobalSearchMaxLength?: boolean;
  isClearSearch?: boolean;
  debounceDelay?: number;
  minWidth?: number | string;
  isValid?: boolean;
} & AutocompleteProps<string, true, true, true>;

const useStyles = makeStyles((theme: DLSTheme) => ({
  autocompleteRoot : {
    height   : '100%',
    minWidth : ({minWidth}: {minWidth: string | number}) => minWidth,
    width    : ({width}: {width: string | number}) => width
  },
  deleteIcon : {
    marginBottom : 3
  },
  input : {
    paddingLeft     : '6px !important',
    paddingRight    : '10px !important',
    color           : dlsColor('grey3'),
    textOverflow    : 'ellipsis',
    overflow        : 'hidden',
    whiteSpace      : 'nowrap',
    width           : 'calc(95% - 70px) !important',
    '&:placeholder' : {
      color        : dlsColor('grey3'),
      textOverflow : 'ellipsis',
      overflow     : 'hidden',
      whiteSpace   : 'nowrap',
      width        : 'calc(80% - 35px)'
    },
    [theme.breakpoints.down('sm')] : {
      width : 'calc(80% - 35px) !important'
    }
  },
  inputRoot : {
    padding    : '0px !important',
    height     : '100%',
    flexWrap   : 'nowrap',
    '&:before' : {
      borderBottom : 'none'
    },
    '&&&&' : {
      '&:hover' : {
        '&:before,&:after' : {
          borderBottom : `solid 2px ${dlsColor('caBlue')}`,
          transition   : 'none'
        }
      }
    }
  },
  inputUnderline : {
    '&:after' : {
      borderBottom : `solid 2px ${dlsColor('caBlue')}`
    }
  },
  trackingSearchBar : {
    width : '100%'
  },
  popupIndicator : {
    display : 'none'
  },
  autocompleteTextFieldRoot : {
    height : '100%'
  },
  closeIcon : {
    paddingRight                   : '5px',
    position                       : 'relative',
    top                            : 'auto',
    [theme.breakpoints.down('sm')] : {
      paddingRight : '20px'
    }
  },
  clearIndicator : {
    visibility : ({searchValue}: {searchValue: string}) => searchValue ? 'visible' : 'hidden'
  },
  searchIcon : {
    padding        : '12px 0 12px 12px',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  searchOption : {
    '&:hover' : {
      backgroundColor : dlsAlt('components', 'icon')?.selected
    },
    '&:[data-focus="true"]' : {
      backgroundColor : dlsColor('darkBlue')
    }
  },
  searchResultsPaper : {
    padding : ({fullWidthOnOpen}: {fullWidthOnOpen?: boolean}) =>
      fullWidthOnOpen ? '28px 46px !important' : 'inherit',
    boxShadow                      : '0px 3px 0px -7px rgba(0,0,0,0.2), 0px 2px 1px 0px rgba(0,0,0,0.14), 0px 2px 3px 0px rgba(0,0,0,0.12)',
    margin                         : '0px 0px 4px 0px',
    borderBottomLeftRadius         : '4px',
    borderBottomRightRadius        : '4px',
    borderTopLeftRadius            : '0px',
    borderTopRightRadius           : '0px',
    [theme.breakpoints.down('sm')] : {
      '&&' : {
        padding : '26px 20px 26px 0px !important',
        height  : '100%'
      }
    }
  },
  noOptionsText : {
    textTransform : 'uppercase',
    color         : dlsColor('grey4'),
    fontWeight    : 700
  },
  listbox : {
    maxHeight                      : '50vh',
    [theme.breakpoints.down('sm')] : {
      height    : '100%',
      maxHeight : '100vh'
    }
  },
  customPaperWrapper : {
    backgroundColor : dlsColor('white')
  },
  customPaperTitle : {
    padding                        : '28px 46px 0 46px !important',
    textTransform                  : 'uppercase',
    fontWeight                     : 'bold',
    fontSize                       : '14px',
    fontFamily                     : 'Roboto',
    color                          : dlsColor('grey4'),
    boxShadow                      : '0px 0px 0px 0px rgba(0,0,0,0.2), 0px 2px 1px 0px rgba(0,0,0,0.14), 0px 2px 3px 0px rgba(0,0,0,0.12)',
    [theme.breakpoints.down('sm')] : {
      '&&' : {
        padding : '26px 20px 0 20px !important'
      }
    }
  },
  customPaperTitleCTC : {
    padding                        : '28px 46px 0 46px !important',
    fontWeight                     : 'bold',
    fontSize                       : '14px',
    fontFamily                     : 'Roboto',
    color                          : dlsColor('grey4'),
    boxShadow                      : '0px 0px 0px 0px rgba(0,0,0,0.2), 0px 2px 1px 0px rgba(0,0,0,0.14), 0px 2px 3px 0px rgba(0,0,0,0.12)',
    [theme.breakpoints.down('sm')] : {
      '&&' : {
        padding : '26px 20px 0 20px !important'
      }
    }
  },
  inputOverlay : {
    position       : 'absolute',
    right          : '100px',
    color          : dlsColor('grey4'),
    fontSize       : '.8rem',
    height         : '40px',
    zIndex         : dlsZIndex('globalGroupChooserSearchInputOverlay'),
    display        : 'flex',
    justifyContent : 'flex-end',
    alignItems     : 'center'
  }
}) as GenericObject);

const getDisplayName = ({isAsset, isScDevice, item, option, searchValue, displayName}) => {
  const isSCDeviceType = isScDevice || item?.type === AssetType.ScDevice;
  if (isAsset || isSCDeviceType) {
    if (isSCDeviceType && item) {
      const {name} = item;
      return displayName || name;
    }
    return item?.displayName || item?.assetName;
  }
  return option?.name;
};

const getList = (hasList, isScDevice, item) => {
  if (isScDevice) {
    return item?.assetName ?? '';
  }
  return hasList ? item?.[GlobalSearchMapping.assets] : '';
};

export const AutocompleteSearch = ({
  onFocus,
  onBlur,
  onResultSelect,
  onChange,
  portalClassOverride,
  recentSearches,
  shortPlaceholder,
  onDelete,
  searchText = '',
  isValid = true,
  isJobSearch = false,
  chipsEnabled = false,
  chipFieldLabels = {},
  filterChips = [],
  options = [],
  width = '100%',
  minWidth = '100%',
  fullWidthOnOpen = false,
  disablePopperPortal = false,
  overrideTitleStyles = false,
  loading = false,
  isCtcAdminGlobalSearch = false,
  isGlobalSearchMaxLength = false,
  loadingText = translate('common:search.loadingTextDefault'),
  noResultsText = translate('common:search.noResultsTextDefault'),
  recentSearchesTitle = translate('common:search.recentlySearched'),
  inputLength = 3,
  debounceDelay = 800,
  isClearSearch = false
}: AutocompleteSearchType) => {
  const searchTranslations                    = translate('common:search', {returnObjects: true, search: searchText});
  const [selectedValue, setSelectedValue]     = useState(null);
  const [searchValue, setSearchValue]         = useState('');
  const [showOverlay, setShowOverlay]         = useState('');
  const [localLoading, setLocalLoading]       = useState(false);
  const classes                               = useStyles({fullWidthOnOpen, searchValue, width, minWidth});

  // Because of the debounce we need to set
  // a loading state right when the user types
  // so we need to keep a local search loading state
  // but ignore it when the global loading state
  // takes over. This hook resets the local state.
  useEffect(() => {
    loading && setLocalLoading(false);
  }, [loading, localLoading]);

  useEffect(() => {
    if (isClearSearch && selectedValue) {
      setSelectedValue(null);
    }
  }, [isClearSearch, selectedValue]);

  const search = useCallback((
    e: ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setSearchValue(value);
    onChange(e, value, reason);
  }, []);

  const handleChange = useCallback(debounce((
    e: ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    const valueLength = value?.length;
    if (!valueLength || valueLength >= inputLength) {
      setShowOverlay('');
      setLocalLoading(true);
      search(e, value, reason);
    }
    if (valueLength && valueLength < inputLength) {
      setShowOverlay(translate('common:search.keepTyping'));
    }
  }, debounceDelay), []);

  const handleOnChange = useCallback((e, value, reason) => {
    setSelectedValue(value);
    onResultSelect(e, value, reason);
    if (isCtcAdminGlobalSearch && isObject(value)) {
      search(e, value.name, reason);
    }
  }, []);

  const handleFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
  }, [onBlur]);

  const CustomPaper = useCallback((props: any) => {
    return !searchValue ? (
      <div className={classes.customPaperWrapper}>
        <div className={overrideTitleStyles ? classes.customPaperTitleCTC : classes.customPaperTitle}>
          {
            (!options?.length && !recentSearches?.length)
              ? translate('common:search.startTyping')
              : recentSearchesTitle
          }
        </div>
        <Paper {...props} />
      </div>
    ) : (<Paper {...props} />);
  }, [searchValue, overrideTitleStyles, options, recentSearches, recentSearchesTitle]);

  const getNoOptionsText = useCallback((): string => {
    if (!options?.length && !recentSearches?.length) {
      return '';
    }
    return loading || localLoading ? loadingText : noResultsText;
  }, [options, recentSearches, loading, localLoading]);

  const getPlaceHolders = useCallback((): string => {
    if (isJobSearch) {
      if (shortPlaceholder) {
        return searchTranslations.searchPlaceholderLong;
      } else if (!filterChips?.length) {
        return searchTranslations.searchPlaceholderLong;
      }
      return '';
    }
    if (shortPlaceholder) {
      return searchTranslations.searchPlaceholderShort;
    }
    return searchTranslations.searchPlaceholderLong;
  }, [shortPlaceholder, isJobSearch, filterChips]);
  
  const searchResultValidator = useCallback(option => {
    const {item, type, hasList, isAsset, isScDevice, displayName} = option;
    const assetType = item?.assetType;
    const assetAlias = item?.assetAlias;
    const iconType  = type !== AssetType.ScDevice ? (
      assetType?.toLowerCase() === 'trailer' ? assetType : type
    ) : SearchType.scdevice;
    const name = getDisplayName({
      isAsset,
      isScDevice,
      item,
      displayName,
      option,
      searchValue
    });
    const lists = getList(hasList, isScDevice, item);

    return {
      ...option,
      type    : iconType,
      name    : (assetAlias && isAsset) ? `${name} (${assetAlias})` : name,
      list    : lists,
      hasList : hasList
    };
  }, [searchValue]);

  const getOptionLabel = useCallback((option: Option) => {
    const {name, displayName} = option || {};
    return displayName || name || '';
  }, []);

  return (
    <div className={classes.trackingSearchBar}>
      {showOverlay && (
        <span className={classes.inputOverlay}>
          {showOverlay}
        </span>
      )}
      <Autocomplete selectOnFocus
        openOnFocus
        disableListWrap
        clearOnBlur
        blurOnSelect
        disablePortal={disablePopperPortal}
        getOptionDisabled={function(option) { return option.isDisabled; }}
        classes={{
          popupIndicator : classes.popupIndicator,
          inputRoot      : classes.inputRoot,
          input          : classes.input,
          root           : classes.autocompleteRoot,
          endAdornment   : classes.closeIcon,
          clearIndicator : classes.clearIndicator,
          option         : classes.searchOption,
          paper          : classes.searchResultsPaper,
          popper         : portalClassOverride,
          noOptions      : classes.noOptionsText,
          listbox        : classes.listbox
        }}
        options={searchValue ? options : recentSearches}
        filterOptions={function(options) { return options; }}
        getOptionLabel={getOptionLabel}
        noOptionsText={getNoOptionsText()}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleOnChange}
        onInputChange={handleChange}
        disableClearable={!!chipsEnabled}
        value={!chipsEnabled ? selectedValue : ''}
        renderInput={function(props: {InputProps: InputPropsType, inputProps}) {
          props.InputProps.startAdornment = (
            <div className={classes.searchIcon}>
              <TopNavSearchIcon />
              <Render.If condition={chipsEnabled && filterChips?.length}>
                {filterChips.map((chip) => (
                  <AutocompleteSearchChip chip={chip} onDelete={onDelete} fieldLabels={chipFieldLabels} />
                ))}
              </Render.If>
            </div>
          );
          props.InputProps.endAdornment = (
            <Render.If condition={isJobSearch}>
              <JobSearchEngineTooltip />
            </Render.If>);
          props.InputProps.classes = {
            underline : classes.inputUnderline
          };
          props.InputProps.disableUnderline = true;
          if (isGlobalSearchMaxLength) {
            props.inputProps.maxLength = 20000;
          } else if (isJobSearch) {
            props.inputProps.maxLength = 32000;
          }
          return (
            <TextField classes={{
              root : classes.autocompleteTextFieldRoot
            }} placeholder={getPlaceHolders()} {...props} />
          );
        }}
        renderOption={function(option) {
          return (
            <AutocompleteSearchResult option={option}
              validator={searchResultValidator}
              searchValue={searchValue} />
          );
        }}
        getOptionSelected={function(option: {name: string;}, value: {name: string;}) {
          return isEqual(option, value);
        }}
        PaperComponent={isValid ? CustomPaper : function() { return null; }} />
    </div>
  );
};

export default AutocompleteSearch;
